import { PlusIcon, XIcon } from "@heroicons/react/outline";
import { observer } from "mobx-react-lite";

import SourcesField from "../SourcesField";
import SelectSource, { SourceType } from "../SourcesField/SelectSource";
import ValidatedField from "../ValidatedField";
import { useBlockValidation } from "../block-store";
import { useSourceSelection } from "../hooks/useSourceSelection";
import { useIsReadonly } from "../readonly-context";
import { FieldType } from "../types";
import { flattenSources } from "../utils/sourceUtils";

import { Button } from "@/components/ui/button";

interface BaseSourceSelectorProps {
  sources: Record<SourceType, string[]>;
  disabled?: boolean;
  validation?: {
    isValid: boolean;
    message?: string;
  };
}

// Single Source Selector
interface SingleSourceSelectorProps extends BaseSourceSelectorProps {
  sourceKey?: string;
  name?: string;
  placeholder?: string;
  validationPath?: string; // Path for validation
  fieldType?: FieldType;
  forceOpen?: boolean;
}

export const SingleSourceSelector = observer(
  ({
    sources,
    sourceKey = "source",
    name,
    disabled = false,
    validation,
    placeholder,
    validationPath,
    fieldType = FieldType.Default,
    forceOpen = false,
  }: SingleSourceSelectorProps) => {
    const { values, handleSingleSourceChange } = useSourceSelection({
      config: { type: "single", key: sourceKey },
      availableSources: sources,
    });

    const { isFieldValid, getFieldError } = useBlockValidation();
    const isReadonly = useIsReadonly();

    // Determine validation state
    const isValid = validationPath ? isFieldValid(validationPath) : (validation?.isValid ?? true);
    const errorMessage = validationPath ? getFieldError(validationPath) : validation?.message;

    const selectedValue = (values as string) || undefined;

    const content = (
      <>
        <SelectSource
          value={selectedValue}
          sources={sources}
          onValueChange={handleSingleSourceChange}
          placeholder={placeholder || `Select a source...`}
          disabled={disabled || isReadonly}
          type={SourceType.Column}
          className="w-full"
          fieldType={fieldType}
          forceOpen={forceOpen}
        />
        {!isValid && (
          <div className="mt-1 text-left text-xs text-red-500">
            {errorMessage || `Source is required`}
          </div>
        )}
      </>
    );

    // If validationPath is provided and name exists, use ValidatedField
    if (validationPath && name) {
      return (
        <ValidatedField name={name} path={validationPath}>
          <SelectSource
            value={selectedValue}
            sources={sources}
            onValueChange={handleSingleSourceChange}
            placeholder={placeholder || `Select a source...`}
            disabled={disabled || isReadonly}
            type={SourceType.Column}
            className="w-full"
            fieldType={fieldType}
            forceOpen={forceOpen}
          />
        </ValidatedField>
      );
    }

    // If no name is provided, render without wrapper for inline use
    if (!name) {
      return <div className="w-full">{content}</div>;
    }

    // Fallback to original SourcesField
    return (
      <SourcesField name={name || "Source"} fieldType={fieldType}>
        {content}
      </SourcesField>
    );
  },
);

// Double Source Selector
interface DoubleSourceSelectorProps extends BaseSourceSelectorProps {
  firstName?: string;
  secondName?: string;
  firstKey?: string;
  secondKey?: string;
  firstValidation?: { isValid: boolean; message?: string };
  secondValidation?: { isValid: boolean; message?: string };
  firstValidationPath?: string;
  secondValidationPath?: string;
  fieldType?: FieldType;
  firstFieldType?: FieldType;
  secondFieldType?: FieldType;
}

export const DoubleSourceSelector = observer(
  ({
    sources,
    firstName = "Source",
    secondName = "Target",
    firstKey,
    secondKey,
    disabled = false,
    firstValidation,
    secondValidation,
    firstValidationPath,
    secondValidationPath,
    fieldType = FieldType.Default,
    firstFieldType,
    secondFieldType,
  }: DoubleSourceSelectorProps) => {
    const { values, handleDoubleSourceChange } = useSourceSelection({
      config: { type: "double", firstKey, secondKey },
      availableSources: sources,
    });

    const isReadonly = useIsReadonly();

    const { first, second } = (values as { first: string; second: string }) || {
      first: null,
      second: null,
    };

    return (
      <SourcesField fieldType={fieldType}>
        <div className="space-y-3">
          {firstValidationPath ? (
            <ValidatedField name={firstName} path={firstValidationPath}>
              <SelectSource
                value={first || undefined}
                sources={sources}
                onValueChange={value => handleDoubleSourceChange(0, value)}
                placeholder="Select a source..."
                disabled={disabled || isReadonly}
                type={SourceType.Column}
                className="w-full"
                fieldType={firstFieldType || fieldType}
              />
            </ValidatedField>
          ) : (
            <div>
              <SelectSource
                name={firstName}
                value={first || undefined}
                sources={sources}
                onValueChange={value => handleDoubleSourceChange(0, value)}
                placeholder="Select a source..."
                disabled={disabled || isReadonly}
                fieldType={firstFieldType || fieldType}
              />
              {firstValidation && !firstValidation.isValid && (
                <div className="mt-1 text-xs text-red-500">
                  {firstValidation.message ||
                    `${firstName} source input is required for comparison`}
                </div>
              )}
            </div>
          )}

          {secondValidationPath ? (
            <ValidatedField name={secondName} path={secondValidationPath}>
              <SelectSource
                value={second || undefined}
                sources={sources}
                onValueChange={value => handleDoubleSourceChange(1, value)}
                placeholder="Select a source..."
                disabled={disabled || isReadonly}
                type={SourceType.Column}
                className="w-full"
                fieldType={secondFieldType || fieldType}
              />
            </ValidatedField>
          ) : (
            <div>
              <SelectSource
                name={secondName}
                value={second || undefined}
                sources={sources}
                onValueChange={value => handleDoubleSourceChange(1, value)}
                placeholder="Select a source..."
                disabled={disabled || isReadonly}
                fieldType={secondFieldType || fieldType}
              />
              {secondValidation && !secondValidation.isValid && (
                <div className="mt-1 text-xs text-red-500">
                  {secondValidation.message ||
                    `${secondName} source input is required for comparison`}
                </div>
              )}
            </div>
          )}
        </div>
      </SourcesField>
    );
  },
);

// Array Source Selector
interface ArraySourceSelectorProps extends BaseSourceSelectorProps {
  min?: number;
  max?: number;
  fieldName?: string;
  fieldType?: FieldType;
}

export const ArraySourceSelector = observer(
  ({
    sources,
    min = 1,
    max,
    disabled = false,
    fieldType = FieldType.Default,
  }: ArraySourceSelectorProps) => {
    const {
      values,
      handleArraySourceChange,
      handleArraySourceAdd,
      handleArraySourceRemove,
      getAvailableSourcesForIndex,
    } = useSourceSelection({
      config: { type: "array", min, max },
      availableSources: sources,
    });

    const isReadonly = useIsReadonly();
    const sourcesArray = (values as string[]) || [""];
    const canAdd = !max || sourcesArray.length < max;
    const canRemove = sourcesArray.length > min;

    const flattenedSources = flattenSources(sources);
    const notYetChosenSources = flattenedSources.filter(source => !sourcesArray.includes(source));

    return (
      <div className="space-y-3">
        {sourcesArray.map((source: string, index: number) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className="flex items-center justify-between gap-x-2"
          >
            <div className="flex-1">
              <SelectSource
                value={source || undefined}
                sources={getAvailableSourcesForIndex(index)}
                onValueChange={value => handleArraySourceChange(index, value)}
                placeholder="Select a source..."
                disabled={disabled || isReadonly}
                type={SourceType.Column}
                className="w-full"
                fieldType={fieldType}
              />
            </div>
            {canRemove && !isReadonly && (
              <button
                onClick={() => handleArraySourceRemove(index)}
                className="flex h-8 w-8 items-center justify-center rounded text-red-500 hover:bg-red-50"
                type="button"
                disabled={disabled}
                title="Remove source"
              >
                <XIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}

        {!disabled && !isReadonly && canAdd && notYetChosenSources.length > 0 && (
          <Button variant="outline" size="sm" onClick={handleArraySourceAdd} className="w-full">
            <PlusIcon className="mr-2 h-4 w-4" />
            Add Source
          </Button>
        )}
      </div>
    );
  },
);
